import { c as _c } from "react/compiler-runtime";
import type { TFunction } from 'i18next';
import type { SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { CartItemGiftLimitFrequencyEnum, GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildGiftLimitItem, DefaultPaymentOptionSet, MinMaxAmount } from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import type { GlobalSettings } from '@sola/schema/dotcom/shared/content/settings.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { useContentSettings } from '@sola/sdg-sdk/hooks.ts';
import { GiftIconMap } from '@sola/shared-utils/cart/gifts.ts';
import { ButtonAction } from '../../types/gifts.ts';
export const givingFrequencyOptions = [{
  value: CommitmentFrequencyEnum.OneTime,
  translationKey: 'core:frequencies.oneTime'
}, {
  value: CommitmentFrequencyEnum.Annually,
  translationKey: 'core:frequencies.annually'
}];
export const giftAmountOptionsBase: Record<string, SelectItemProps> = {
  none: {
    value: '',
    text: 'Select an amount'
  },
  other: {
    value: 'Other',
    text: 'Other'
  }
};
export const giftAmountsFallback: Record<GiftTypeEnum, {
  giftAmounts: number[];
  giftAmountDefault: number;
}> = {
  [GiftTypeEnum.Birthday]: {
    giftAmounts: [10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150],
    giftAmountDefault: 30
  },
  [GiftTypeEnum.Child]: {
    giftAmounts: [10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150],
    giftAmountDefault: 50
  },
  [GiftTypeEnum.Family]: {
    giftAmounts: [25, 50, 60, 75, 100, 150, 200, 250, 300, 400, 500, 600, 700, 800, 900, 1100],
    giftAmountDefault: 75
  },
  [GiftTypeEnum.Center]: {
    giftAmounts: [50, 100, 150, 200, 250, 300, 400, 500, 1000, 2000],
    giftAmountDefault: 100
  },
  // TODO: Confirm Final amounts & default values
  [GiftTypeEnum.Final]: {
    giftAmounts: [25, 50, 75, 100, 150, 200, 250, 300, 400, 500, 600, 700, 800, 900, 1100],
    giftAmountDefault: 100
  }
};
const giftTypeToJsonKey: Record<GiftTypeEnum, keyof NonNullable<GlobalSettings['giftAmounts']>> = {
  [GiftTypeEnum.Birthday]: 'birthday',
  [GiftTypeEnum.Center]: 'center',
  [GiftTypeEnum.Child]: 'child',
  [GiftTypeEnum.Family]: 'family',
  [GiftTypeEnum.Final]: 'final'
};
const resolveGiftAmounts = (giftType: GiftTypeEnum, settings: GlobalSettings | undefined): {
  giftAmounts: number[];
  giftAmountDefault: number;
} => {
  const fallback = giftAmountsFallback[giftType];
  const json = settings?.giftAmounts?.[giftTypeToJsonKey[giftType]];
  const options = json?.options?.map(Number).filter(n => Number.isFinite(n));
  const parsedDefault = Number(json?.default);
  return {
    giftAmounts: options?.length ? options : fallback.giftAmounts,
    giftAmountDefault: Number.isFinite(parsedDefault) ? parsedDefault : fallback.giftAmountDefault
  };
};

/**
 * Maps a CommitmentFrequencyEnum value to the corresponding key in
 * DefaultPaymentOptionSet so we can look up min/max for the selected frequency.
 */
const frequencyToOptionKey: Record<CommitmentFrequencyEnum, keyof DefaultPaymentOptionSet> = {
  [CommitmentFrequencyEnum.OneTime]: CartItemGiftLimitFrequencyEnum.OneTime,
  [CommitmentFrequencyEnum.Monthly]: CartItemGiftLimitFrequencyEnum.Monthly,
  [CommitmentFrequencyEnum.Quarterly]: CartItemGiftLimitFrequencyEnum.Quarterly,
  [CommitmentFrequencyEnum.Semiannually]: CartItemGiftLimitFrequencyEnum.Semiannually,
  [CommitmentFrequencyEnum.Annually]: CartItemGiftLimitFrequencyEnum.Annually
};

/**
 * Resolves the min/max amounts for the custom "Other" field based on the
 * gift type limits and the currently selected frequency.
 */
export const getCustomAmountLimits = (giftTypeLimits: ChildGiftLimitItem | undefined, frequency: CommitmentFrequencyEnum): MinMaxAmount | undefined => {
  if (!giftTypeLimits) {
    return undefined;
  }
  const key = frequencyToOptionKey[frequency];
  return giftTypeLimits.paymentOptionsMinMax?.[key];
};
export const useGiftData = (t, giftType, commitment, buttonAction) => {
  const $ = _c(29);
  const {
    data: contentSettings
  } = useContentSettings();
  const childName = commitment.participant?.names?.preferred ?? commitment.participant?.name ?? "";
  const gender = commitment.participant?.gender ?? "";
  let t0;
  if ($[0] !== giftType) {
    t0 = giftType.toLowerCase();
    $[0] = giftType;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const lowerType = t0;
  const icon = GiftIconMap[giftType];
  const isSetupRecurring = buttonAction === ButtonAction.SetupRecurring;
  const isEdit = buttonAction === ButtonAction.Edit;
  let t1;
  if ($[2] !== contentSettings || $[3] !== giftType) {
    t1 = resolveGiftAmounts(giftType, contentSettings);
    $[2] = contentSettings;
    $[3] = giftType;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    giftAmounts,
    giftAmountDefault
  } = t1;
  let t2;
  if ($[5] !== giftType) {
    t2 = giftType === GiftTypeEnum.Birthday ? [CommitmentFrequencyEnum.OneTime, CommitmentFrequencyEnum.Annually] : [CommitmentFrequencyEnum.OneTime];
    $[5] = giftType;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const frequencies = t2;
  let t3;
  if ($[7] !== isEdit || $[8] !== isSetupRecurring || $[9] !== lowerType || $[10] !== t) {
    t3 = isSetupRecurring ? t("gifts.dialog.setupRecurringAction") : t(`gifts.${lowerType}.dialog.${isEdit ? "editAction" : "action"}`);
    $[7] = isEdit;
    $[8] = isSetupRecurring;
    $[9] = lowerType;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const addButtonText = t3;
  const t4 = `gifts.${lowerType}.dialog.title`;
  let t5;
  if ($[12] !== childName || $[13] !== t || $[14] !== t4) {
    t5 = t(t4, {
      childName
    });
    $[12] = childName;
    $[13] = t;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const t6 = `gifts.${lowerType}.dialog.supportingText`;
  let t7;
  if ($[16] !== childName || $[17] !== gender || $[18] !== t || $[19] !== t6) {
    t7 = t(t6, {
      context: gender,
      childName
    });
    $[16] = childName;
    $[17] = gender;
    $[18] = t;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== addButtonText || $[22] !== frequencies || $[23] !== giftAmountDefault || $[24] !== giftAmounts || $[25] !== icon || $[26] !== t5 || $[27] !== t7) {
    t8 = {
      icon,
      title: t5,
      supportingText: t7,
      addButtonText,
      giftAmounts,
      giftAmountDefault,
      acceptCustomValue: true,
      frequencies
    };
    $[21] = addButtonText;
    $[22] = frequencies;
    $[23] = giftAmountDefault;
    $[24] = giftAmounts;
    $[25] = icon;
    $[26] = t5;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
};